import { Box, Tooltip } from "@mui/material";

import { useNavigate } from "react-router-dom";
import EditableFieldString from "./EditableFieldString";
import { renderType } from "common/renderType";
import NiceBoolean from "./NiceBoolean";
import NiceId from "./NiceId";
import { isLongMachineValue } from "common/longMachineValue";
import NiceDate from "./NiceDate";
import NiceDateUTC from "./NiceDateUTC";
import NiceMultiLine from "./NiceMultiLine";
import EditableFieldNumber from "./EditableFieldNumber";
import EditableFieldBool from "./EditableFieldBool";
import EditableFieldDate from "./EditableFieldDate";
import EditableFieldAzureId from "./EditableFieldAzureId";
import EditableFieldFile from "./EditableFieldFile";
import EditableFieldForm from "./EditableFieldForm";
import { processString } from "common/helpers";
import NiceLink from "./NiceLink";
import NiceTagValue from "./NiceTagValue";

//CorrectLable: make the lable have title case with spaces
const KeyValValue = ({
  value,
  link,
  tip,
  makeBlue,
  fieldType,
  field,
  updater,
  serviceImplemenator,
  serviceType,
  serviceDimension,
  serviceFilter,
  lookUpLabel,
  uiRenderType,
  canEdit,
  multiLine,
  rows,
  currencyCode,
  required,
  setIsUpdating,

  makeLink,
  makeLinkIcon,
  linkLabel,
  uiFocusLevel,
  rowData,
  isArray,
  maxLength,
  allowedMimeTypes,
  maxFileSize,
  formReference,
  aliasName,
}) => {
  const navigate = useNavigate();

  const valueType = typeof value;

  let renderedValue = value;

  if (uiRenderType) {
    //check for a NiceActive, this will not render as a key vall

    if (uiRenderType === "<NiceActive>") {
      renderedValue = <NiceBoolean>{value}</NiceBoolean>;
    } else if (uiRenderType === "<NiceDate>") {
      // Relative time is opted into HERE rather than defaulted in NiceDate,
      // because renderType is shared with DataGrid columns (renderColumn.js,
      // WidgetTable.jsx). Defaulting it on appended "3 days ago" to every date
      // cell in every grid, widening and truncating them app-wide.
      renderedValue = <NiceDate relative>{value}</NiceDate>;
    } else if (uiRenderType === "<NiceDateUTC>") {
      renderedValue = <NiceDateUTC relative>{value}</NiceDateUTC>;
    } else {
      renderedValue = renderType(uiRenderType, value, currencyCode);
    }
  } else {
    if (multiLine || rows > 1) {
      renderedValue = <NiceMultiLine>{value}</NiceMultiLine>;
    } else if (value !== null && typeof value === "object") {
      // Tag-shaped values (a flat key/value bag) become chips; anything else --
      // arrays, nested structures -- keeps the pretty-printed JSON this branch
      // used to emit unconditionally. Ten tag rows of three-line JSON blobs was
      // the single worst thing on a Cloud Org Structure detail page.
      renderedValue = <NiceTagValue>{value}</NiceTagValue>;
    } else if (isLongMachineValue(value)) {
      // A long unbreakable string has no wrap opportunity, so it pushes past
      // the card instead of wrapping inside it -- a flattened Azure resource
      // id is ~130 characters with no spaces. NiceId gives it monospace,
      // single-line truncation and a copy button.
      //
      // Routed on shape rather than on metadata deliberately: the kernel's
      // uiRenderCell is "" for cloudOrgStructure's id and no def anywhere
      // declares <NiceId>, so keying off metadata would render nothing until
      // every def is retagged. This covers all 14 ObjectDetails scenes today.
      renderedValue = <NiceId>{value}</NiceId>;
    }
  }





  return (
    <Box>
      {makeLink ? (
        <NiceLink
          label={
            linkLabel
              ? linkLabel !== ""
                ? processString(linkLabel, rowData)
                : renderedValue
              : renderedValue
          }
          link={processString(link, rowData)}
          tip={processString(tip, rowData)}
          makeBlue={makeBlue}
          showFollowIcon={makeLinkIcon}
        />
      ) : (
        <Box>
          {updater === undefined || !canEdit ? (
            <Tooltip title={tip}>
              <Box>{renderedValue}</Box>
            </Tooltip>
          ) : fieldType === "string" ? (
            <EditableFieldString
              field={field}
              serviceImplemenator={serviceImplemenator}
              serviceDimension={serviceDimension}
              serviceType={serviceType}
              serviceFilter={serviceFilter}
              lookUpLabel={lookUpLabel}
              tip={tip}
              rows={rows}
              fieldType={fieldType}
              initialValue={value}
              displayValue={renderedValue ? renderedValue : "Not Set"}
              currencyCode={currencyCode}
              onChange={(newValue) => {
                if (value !== newValue) {
                  updater(field, newValue);
                }
              }}
              uiRenderType={uiRenderType}
              setIsUpdating={setIsUpdating}
              isArray={isArray}
              maxLength={maxLength}
            />
          ) : fieldType === "number" ? (
            <EditableFieldNumber
              field={field}
              serviceImplemenator={serviceImplemenator}
              serviceDimension={serviceDimension}
              serviceType={serviceType}
              lookUpLabel={lookUpLabel}
              serviceFilter={serviceFilter}
              tip={tip}
              rows={rows}
              fieldType={fieldType}
              initialValue={value}
              displayValue={renderedValue}
              currencyCode={currencyCode}
              onChange={(newValue) => {
                const numericValue = parseFloat(newValue);
                if (!isNaN(numericValue) && value !== numericValue) {
                  updater(field, numericValue);
                }
              }}
              uiRenderType={uiRenderType}
              setIsUpdating={setIsUpdating}
              isArray={isArray}
            />
          ) : fieldType === "boolean" ? (
            <EditableFieldBool
              field={field}
              tip={tip}
              initialValue={value}
              displayValue={renderedValue}
              onChange={(newValue) => {
                const boolNewValue = newValue === "TRUE" ? true : false;
                if (value !== boolNewValue) {
                  updater(field, boolNewValue);
                }
              }}
              setIsUpdating={setIsUpdating}
            />
          ) : fieldType === "date" ? (
            <EditableFieldDate
              field={field}
              tip={tip}
              initialValue={value}
              displayValue={renderedValue}
              renderType={uiRenderType}
              onChange={(newValue) => {
                if (value !== newValue) {
                  updater(field, newValue);
                }
              }}
              setIsUpdating={setIsUpdating}
              isArray={isArray}
            />
          ) : fieldType === "azureId" ? (
            <EditableFieldAzureId
              field={field}
              tip={tip}
              initialValue={value}
              required={required}
              onChange={(newValue) => {
                if (value !== newValue) {
                  updater(field, newValue);
                }
              }}
              setIsUpdating={setIsUpdating}
            />
          ) : fieldType === "file" ? (
            <EditableFieldFile
              field={field}
              tip={tip}
              initialValue={value}
              allowedMimeTypes={allowedMimeTypes}
              maxFileSize={maxFileSize}
              isArray={isArray}
              uiRenderType={uiRenderType}
              onChange={(newValue) => {
                // For file fields, always call updater since the value changes
                // (comparing arrays or "Not Set" to ID doesn't work with simple !==)
                updater(field, newValue);
              }}
              setIsUpdating={setIsUpdating}
            />
          ) : fieldType === "form" ? (
            <EditableFieldForm
              field={field}
              tip={tip}
              initialValue={value}
              formReference={formReference}
              aliasName={aliasName}
              isArray={isArray}
              onChange={(field, newValue) => {
                updater(field, newValue);
              }}
              setIsUpdating={setIsUpdating}
            />
          ) : (
            fieldType
          )}
        </Box>
      )}
    </Box>
  );
};

export default KeyValValue;
